import { works, type Work, type WorkType } from "@/mocks/movies";

export interface FilterParams {
  type?: WorkType | "all";
  genre?: string;
  yearRange?: string;
  query?: string;
  sort?: string;
}

export function getWorkById(id: string): Work | undefined {
  return works.find((w) => w.id === id);
}

export function sortWorks(list: Work[], sort?: string): Work[] {
  const result = [...list];
  if (sort === "latest") {
    return result.sort(
      (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime(),
    );
  }
  if (sort === "rating") {
    return result.sort((a, b) => b.rating - a.rating);
  }
  return result.sort((a, b) => b.popularity - a.popularity);
}

export function filterWorks(params: FilterParams): Work[] {
  let list = [...works];

  if (params.type && params.type !== "all") {
    list = list.filter((w) => w.type === params.type);
  }
  if (params.genre && params.genre !== "all") {
    list = list.filter((w) => w.genres.includes(params.genre as string));
  }
  if (params.yearRange && params.yearRange !== "all") {
    const years = Number(params.yearRange);
    if (!Number.isNaN(years)) {
      const currentYear = new Date().getFullYear();
      list = list.filter((w) => currentYear - w.year <= years);
    }
  }
  if (params.query && params.query.trim()) {
    const q = params.query.trim().toLowerCase();
    list = list.filter(
      (w) =>
        w.title.toLowerCase().includes(q) ||
        w.originalTitle.toLowerCase().includes(q) ||
        w.genres.some((g) => g.toLowerCase().includes(q)) ||
        w.director.toLowerCase().includes(q) ||
        w.cast.some((c) => c.toLowerCase().includes(q)),
    );
  }

  return sortWorks(list, params.sort);
}

export function searchWorks(query: string, type?: WorkType | "all"): Work[] {
  return filterWorks({ query, type, sort: "popularity" });
}

export function getFeatured(): Work[] {
  const featured = works.filter((w) => w.featured);
  return featured.length > 0 ? featured : works.slice(0, 5);
}

export function getPopular(limit = 8): Work[] {
  return sortWorks(works, "popularity").slice(0, limit);
}

export function getLatest(limit = 8): Work[] {
  return sortWorks(works, "latest").slice(0, limit);
}

export function getRelated(work: Work, limit = 6): Work[] {
  const related = works
    .filter((w) => w.id !== work.id && w.genres.some((g) => work.genres.includes(g)))
    .sort((a, b) => b.popularity - a.popularity);
  if (related.length >= limit) return related.slice(0, limit);
  const fillers = sortWorks(
    works.filter((w) => w.id !== work.id && !related.includes(w)),
    "popularity",
  );
  return [...related, ...fillers].slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export const PAGE_SIZE = 9;